/**
 * Locale-aware money formatting for client-side rendered prices.
 *
 * Single JS chokepoint mirroring the server-side snippets/money-localized.liquid.
 * Both use CLDR (via Intl.NumberFormat with useGrouping:'always') as the source of
 * truth, so SSR and client output match for every Shopify market/locale.
 *
 * Context comes from the active Shopify market:
 *   - window.themeVariables.locale    -> "{lang}-{country}" (e.g. "de-AT"), set in snippets/js-variables.liquid
 *   - window.themeVariables.currency  -> presentment currency ISO code
 * with Shopify.* and <html lang> as fallbacks.
 *
 * Usage:
 *   window.theme.formatMoney(9553.46)                       // major units -> "9.553,46 €"
 *   window.theme.formatMoneyCents(955346)                   // Shopify cents/subunits
 *   window.theme.formatMoney(amount, { currency, locale })  // explicit overrides
 */
(function () {
  window.theme = window.theme || {};

  var formatterCache = {};

  // Shopify's zero-decimal currencies. Everything else is treated as 2-decimal
  // (matching the server-side map and Shopify money filters), so the decimal
  // count does not drift with the browser's ICU/CLDR version (e.g. HUF).
  var ZERO_DECIMAL_CURRENCIES = {
    BIF: 1, CLP: 1, DJF: 1, GNF: 1, ISK: 1, JPY: 1, KMF: 1, KRW: 1,
    PYG: 1, RWF: 1, UGX: 1, UYI: 1, VND: 1, VUV: 1, XAF: 1, XOF: 1, XPF: 1,
  };

  // Business overrides where the reference table diverges from raw CLDR/Intl.
  // Keep in sync with OVERRIDES in scripts/generate-money-format-map.mjs.
  // nb-NO: reference table shows "9 553,46 kr" (symbol after); CLDR puts it before.
  var SYMBOL_POSITION_OVERRIDE = { 'nb-NO': 'after' };

  // Reorder an Intl.formatToParts result so the currency symbol sits at `position`
  // ('before' | 'after'), reusing the existing adjacent spacer literal.
  function formatWithPosition(formatter, value, position) {
    var parts = formatter.formatToParts(value);
    var curIdx = -1;
    for (var i = 0; i < parts.length; i++) {
      if (parts[i].type === 'currency') { curIdx = i; break; }
    }
    if (curIdx === -1) {
      return parts.map(function (p) { return p.value; }).join('');
    }
    var symbol = parts[curIdx].value;
    var spacer = '';
    var dropAdjacent = -1;
    if (position === 'after' && parts[curIdx + 1] && parts[curIdx + 1].type === 'literal') {
      spacer = parts[curIdx + 1].value;
      dropAdjacent = curIdx + 1;
    } else if (position === 'before' && parts[curIdx - 1] && parts[curIdx - 1].type === 'literal') {
      spacer = parts[curIdx - 1].value;
      dropAdjacent = curIdx - 1;
    }
    var number = parts
      .filter(function (p, idx) { return idx !== curIdx && idx !== dropAdjacent; })
      .map(function (p) { return p.value; })
      .join('');
    if (!spacer) { spacer = '\u00A0'; }
    return position === 'after' ? number + spacer + symbol : symbol + spacer + number;
  }

  function activeContext() {
    var tv = window.themeVariables || {};
    var shopify = window.Shopify || {};
    var locale =
      tv.locale ||
      shopify.locale ||
      (document.documentElement && document.documentElement.lang) ||
      'en';
    var currency =
      tv.currency ||
      (shopify.currency && (shopify.currency.active || shopify.currency.currency)) ||
      'EUR';
    return { locale: locale, currency: currency };
  }

  function getFormatter(locale, currency, intlOptions) {
    var key = locale + '|' + currency + '|' + (intlOptions ? JSON.stringify(intlOptions) : '');
    if (formatterCache[key]) {
      return formatterCache[key];
    }

    var decimals = ZERO_DECIMAL_CURRENCIES[currency] ? 0 : 2;
    var options = {
      style: 'currency',
      currency: currency,
      useGrouping: 'always',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    };
    if (intlOptions) {
      for (var prop in intlOptions) {
        if (Object.prototype.hasOwnProperty.call(intlOptions, prop)) {
          options[prop] = intlOptions[prop];
        }
      }
    }

    var formatter = null;
    try {
      formatter = new Intl.NumberFormat(locale, options);
    } catch (error) {
      try {
        // Retry without the (rarely unsupported) locale, keeping the currency.
        formatter = new Intl.NumberFormat(undefined, options);
      } catch (fallbackError) {
        formatter = null;
      }
    }

    formatterCache[key] = formatter;
    return formatter;
  }

  /**
   * Format an amount given in the currency's MAJOR unit (e.g. 9553.46).
   * @param {number|string} amount
   * @param {{ currency?: string, locale?: string, intlOptions?: object }} [options]
   * @returns {string}
   */
  window.theme.formatMoney = function (amount, options) {
    options = options || {};
    var context = activeContext();
    var locale = options.locale || context.locale;
    var currency = options.currency || context.currency;

    var value = Number(amount);
    if (!isFinite(value)) {
      value = 0;
    }

    var formatter = getFormatter(locale, currency, options.intlOptions);
    if (formatter) {
      var positionOverride = SYMBOL_POSITION_OVERRIDE[locale];
      if (positionOverride) {
        return formatWithPosition(formatter, value, positionOverride);
      }
      return formatter.format(value);
    }

    // Last-resort fallback (Intl unavailable): amount + non-breaking space + code.
    return value.toFixed(2) + '\u00A0' + currency;
  };

  /**
   * Format a Shopify price given in the currency SUBUNIT (cents).
   * @param {number|string} cents
   * @param {{ currency?: string, locale?: string, intlOptions?: object }} [options]
   * @returns {string}
   */
  window.theme.formatMoneyCents = function (cents, options) {
    return window.theme.formatMoney((Number(cents) || 0) / 100, options);
  };
})();
